import logger from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/layout/Navbar';
import { MobileNav } from '../components/layout/MobileNav';
import Footer from '../components/layout/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Users, 
  Heart, 
  Target, 
  Lightbulb, 
  Mail, 
  MapPin, 
  Clock, 
  Briefcase,
  FileDown,
  Lock,
  Sparkles,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for dynamic values
const iconMap = {
  Heart, Target, Lightbulb, Users, Mail, Sparkles, MessageSquare, Briefcase
};

export default function JoinUsPage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactEmail, setContactEmail] = useState('internetpme@gmail.com');
  const [pageContent, setPageContent] = useState(null);

  useEffect(() => {
    fetchPositions();
    fetchSiteConfig();
    fetchPageContent();
  }, []);

  const fetchPositions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/positions`);
      if (response.ok) {
        const data = await response.json();
        setPositions(data.positions || []);
      }
    } catch (error) {
      logger.error('Error fetching positions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSiteConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/site-config`);
      if (response.ok) {
        const data = await response.json();
        if (data.contact_email) {
          setContactEmail(data.contact_email);
        }
      }
    } catch (error) {
      logger.error('Error fetching site config:', error);
    }
  };

  const fetchPageContent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/join-us-content`);
      if (response.ok) {
        const data = await response.json();
        setPageContent(data);
      }
    } catch (error) {
      logger.error('Error fetching page content:', error);
    }
  };

  // Helper to get translated text
  const getText = (field, fallback = '') => {
    if (!pageContent || !pageContent[field]) return fallback;
    const value = pageContent[field];
    if (typeof value === 'object') {
      return value[language] || value.fr || value.en || fallback;
    }
    return value || fallback;
  };

  // Generate mailto link with pre-filled subject
  const getMailtoLink = (type) => {
    const buttonTexts = pageContent?.contact_buttons || {};
    const subjects = {
      candidature: getText('contact_buttons', {})?.candidature?.[language] || (language === 'fr' ? 'Candidature spontanée - PayHub Québec+' : 'Spontaneous Application - PayHub Québec+'),
      idea: getText('contact_buttons', {})?.idea?.[language] || (language === 'fr' ? 'Proposition d\'idée - PayHub Québec+' : 'Idea Proposal - PayHub Québec+'),
      suggestion: getText('contact_buttons', {})?.suggestion?.[language] || (language === 'fr' ? 'Suggestion - PayHub Québec+' : 'Suggestion - PayHub Québec+')
    };
    const subject = encodeURIComponent(subjects[type] + ' - PayHub Québec+');
    return `mailto:${contactEmail}?subject=${subject}`;
  };

  const getButtonText = (key) => {
    if (!pageContent?.contact_buttons?.[key]) {
      const defaults = {
        candidature: { fr: "Candidature spontanée", en: "Spontaneous Application" },
        idea: { fr: "Proposer une idée", en: "Propose an Idea" },
        suggestion: { fr: "Faire une suggestion", en: "Make a Suggestion" }
      };
      return defaults[key]?.[language] || key;
    }
    return pageContent.contact_buttons[key][language] || pageContent.contact_buttons[key].fr || key;
  };

  const downloadPdf = async (postingId, title, pdfLang) => {
    if (!user) {
      toast.error(language === 'fr' 
        ? 'Connectez-vous à votre compte pour télécharger' 
        : 'Log in to your account to download');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/positions/${postingId}/pdf?lang=${pdfLang}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `PayHub_Position_${title.replace(/\s+/g, '_').substring(0, 30)}_${pdfLang.toUpperCase()}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success(language === 'fr' ? 'PDF téléchargé!' : 'PDF downloaded!');
      } else {
        throw new Error('Download failed');
      }
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors du téléchargement' : 'Download error');
    }
  };

  // Get values from dynamic content or use defaults
  const getValues = () => {
    if (pageContent?.values && Array.isArray(pageContent.values)) {
      return pageContent.values.map(v => ({
        icon: iconMap[v.icon] || Heart,
        title: v.title?.[language] || v.title?.fr || '',
        text: v.text?.[language] || v.text?.fr || ''
      }));
    }
    // Default values
    return [
      { icon: Heart, title: language === 'fr' ? "Solidarité" : "Solidarity", text: language === 'fr' ? "Nous nous soutenons mutuellement" : "We support each other" },
      { icon: Target, title: language === 'fr' ? "Impact Local" : "Local Impact", text: language === 'fr' ? "Chaque action compte pour notre communauté" : "Every action matters for our community" },
      { icon: Lightbulb, title: language === 'fr' ? "Innovation" : "Innovation", text: language === 'fr' ? "Des solutions modernes pour l'économie locale" : "Modern solutions for local economy" },
      { icon: Users, title: language === 'fr' ? "Collaboration" : "Collaboration", text: language === 'fr' ? "Ensemble, nous sommes plus forts" : "Together, we are stronger" }
    ];
  };

  const values = getValues();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#003DA5]/5 to-background flex flex-col">
      <Navbar />
      
      <main className="flex-1 pt-16">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-[#003DA5]/5"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#003DA5]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          
          <div className="max-w-7xl mx-auto px-4 relative">
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#003DA5]/10 border border-[#003DA5]/20 mb-6">
                <span className="text-2xl">⚜️</span>
                <span className="font-semibold text-[#003DA5]">PayHub Québec+</span>
              </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#003DA5] mb-4">
              {getText('hero_title', language === 'fr' ? 'Rejoignez-nous' : 'Join Us')}
            </h1>
            <p className="text-xl text-muted-foreground">
              {getText('hero_subtitle', language === 'fr' ? "Ensemble, bâtissons l'économie locale du Québec" : "Together, let's build Quebec's local economy")}
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-[#003DA5]/20 bg-gradient-to-br from-[#003DA5]/5 to-transparent">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#003DA5]">
                  <Target className="h-6 w-6" />
                  {getText('mission_title', language === 'fr' ? 'Notre Mission' : 'Our Mission')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed">
                  {getText('mission_text', language === 'fr' 
                    ? "PayHub Québec+ est plus qu'une plateforme de paiement. C'est un mouvement collectif pour soutenir et dynamiser les monnaies locales à travers le Québec." 
                    : "PayHub Québec+ is more than a payment platform. It's a collective movement to support and energize local currencies across Quebec.")}
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-teal-500/20 bg-gradient-to-br from-teal-500/5 to-transparent">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-teal-600">
                  <Lightbulb className="h-6 w-6" />
                  {getText('vision_title', language === 'fr' ? 'Notre Vision' : 'Our Vision')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed">
                  {getText('vision_text', language === 'fr'
                    ? "Créer un réseau interconnecté (Hub & Spokes) où les entreprises locales, les citoyens et les organismes communautaires peuvent échanger, grandir et prospérer ensemble."
                    : "Create an interconnected network (Hub & Spokes) where local businesses, citizens, and community organizations can exchange, grow, and prosper together.")}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            {getText('values_title', language === 'fr' ? 'Nos Valeurs' : 'Our Values')}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => (
              <Card key={idx} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="w-14 h-14 rounded-full bg-[#003DA5]/10 flex items-center justify-center mx-auto mb-4">
                    <value.icon className="h-7 w-7 text-[#003DA5]" />
                  </div>
                  <h3 className="font-semibold mb-2">{value.title}</h3>
                  <p className="text-sm text-muted-foreground">{value.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-2">
              {getText('positions_title', language === 'fr' ? 'Positions Recherchées' : 'Open Positions')}
            </h2>
            <p className="text-muted-foreground">
              {getText('positions_subtitle', language === 'fr' ? 'Découvrez les rôles où vous pouvez contribuer' : 'Discover roles where you can contribute')}
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#003DA5]"></div>
            </div>
          ) : positions.length === 0 ? (
            <Card className="max-w-lg mx-auto text-center">
              <CardContent className="py-12">
                <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold mb-2">
                  {getText('no_positions', language === 'fr' ? 'Aucune position ouverte pour le moment' : 'No open positions at the moment')}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {getText('no_positions_text', language === 'fr' ? 'Mais nous accueillons toujours les candidatures spontanées et vos idées!' : 'But we always welcome spontaneous applications and your ideas!')}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6">
              {positions.map((position) => {
                const title = language === 'en' 
                  ? (position.title_en || position.title_fr || position.title)
                  : (position.title_fr || position.title);
                const description = language === 'en'
                  ? (position.description_en || position.description_fr || position.description)
                  : (position.description_fr || position.description);
                const hasFr = !!(position.title_fr || position.title);
                const hasEn = !!position.title_en;

                return (
                  <Card key={position.posting_id} className="hover:shadow-lg transition-all">
                    <CardContent className="py-6">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="text-xl font-semibold">{title}</h3>
                            <div className="flex gap-1">
                              {hasFr && <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700">FR</span>}
                              {hasEn && <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-700">EN</span>}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-3">
                            <span className="flex items-center gap-1">
                              <Briefcase className="h-4 w-4" />
                              {position.department}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {position.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {position.contract_type}
                            </span>
                          </div>
                          <p className="text-muted-foreground line-clamp-2">{description}</p>
                        </div>
                        
                        <div className="flex gap-2 flex-wrap">
                          {user ? (
                            <>
                              {hasFr && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => downloadPdf(position.posting_id, position.title_fr || position.title, 'fr')}
                                  className="gap-1"
                                >
                                  <FileDown className="h-4 w-4" />
                                  PDF FR
                                </Button>
                              )}
                              {hasEn && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => downloadPdf(position.posting_id, position.title_en, 'en')}
                                  className="gap-1"
                                >
                                  <FileDown className="h-4 w-4" />
                                  PDF EN
                                </Button>
                              )}
                            </>
                          ) : (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Lock className="h-4 w-4" />
                              {language === 'fr' ? 'Connexion requise' : 'Login required'}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Contact Section - mailto buttons */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-2xl mx-auto px-4">
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="flex items-center justify-center gap-2">
                <Mail className="h-5 w-5 text-[#003DA5]" />
                {getText('contact_title', language === 'fr' ? 'Contribuez à Notre Mission' : 'Contribute to Our Mission')}
              </CardTitle>
              <CardDescription>
                {getText('contact_subtitle', language === 'fr' ? 'Écrivez-nous directement par courriel' : 'Write to us directly by email')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Candidature Button */}
              <a
                href={getMailtoLink('candidature')}
                className="flex items-center justify-between w-full p-4 rounded-xl border-2 border-[#003DA5]/20 hover:border-[#003DA5] hover:bg-[#003DA5]/5 transition-all group"
                data-testid="mailto-candidature"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#003DA5]/10 flex items-center justify-center group-hover:bg-[#003DA5]/20 transition-colors">
                    <Users className="h-5 w-5 text-[#003DA5]" />
                  </div>
                  <span className="font-medium">{getButtonText('candidature')}</span>
                </div>
                <Mail className="h-5 w-5 text-muted-foreground group-hover:text-[#003DA5] transition-colors" />
              </a>

              {/* Idea Button */}
              <a
                href={getMailtoLink('idea')}
                className="flex items-center justify-between w-full p-4 rounded-xl border-2 border-teal-500/20 hover:border-teal-500 hover:bg-teal-500/5 transition-all group"
                data-testid="mailto-idea"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-teal-500/10 flex items-center justify-center group-hover:bg-teal-500/20 transition-colors">
                    <Sparkles className="h-5 w-5 text-teal-600" />
                  </div>
                  <span className="font-medium">{getButtonText('idea')}</span>
                </div>
                <Mail className="h-5 w-5 text-muted-foreground group-hover:text-teal-600 transition-colors" />
              </a>

              {/* Suggestion Button */}
              <a
                href={getMailtoLink('suggestion')}
                className="flex items-center justify-between w-full p-4 rounded-xl border-2 border-amber-500/20 hover:border-amber-500 hover:bg-amber-500/5 transition-all group"
                data-testid="mailto-suggestion"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                    <MessageSquare className="h-5 w-5 text-amber-600" />
                  </div>
                  <span className="font-medium">{getButtonText('suggestion')}</span>
                </div>
                <Mail className="h-5 w-5 text-muted-foreground group-hover:text-amber-600 transition-colors" />
              </a>
            </CardContent>
          </Card>
        </div>
      </section>
      </main>
      
      <Footer />
      <MobileNav />
    </div>
  );
}
